import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useForm } from 'react-hook-form';
import { PassportAuth } from '../../contexts/AuthContext';
import { getStorage } from '../../utils/functions/localstorage';
import api from '../../utils/functions/api';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function isTokenExpired(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return !payload.exp || payload.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
}

export default function AuthorizeConsent() {
  const query = useQuery();
  const clientId = query.get('client_id');
  const redirectUri = query.get('redirect_uri');
  const state = query.get('state');

  const { handleSignin, handleSignout } = useContext(PassportAuth);
  const { register, handleSubmit } = useForm();
  const [loggedIn, setLoggedIn] = useState(() => {
    const token = getStorage('token');
    return !!token && !isTokenExpired(token);
  });
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [consentInfo, setConsentInfo] = useState(null);
  const [loadError, setLoadError] = useState('');

  const apiBase = String(process.env.REACT_APP_API_URL || '').trim();

  const fetchAuthorize = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await api.get(
        `${apiBase}/oauth/authorize?client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri)}&state=${encodeURIComponent(state || '')}`,
      );
      setConsentInfo(data);
    } catch (error) {
      setLoadError(error?.response?.data?.error?.message || 'ไม่สามารถโหลดข้อมูล App ได้');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (loggedIn) fetchAuthorize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  const onLoginSubmit = async (data) => {
    setLoginError('');
    try {
      await handleSignin(data.username, data.password);
      setLoggedIn(true);
    } catch (error) {
      setLoginError('ไม่สามารถเข้าสู่ระบบ กรุณาตรวจสอบชื่อผู้ใช้และรหัสผ่าน');
    }
  };

  // Logs out of CorePlan and drops back to the login form on this SAME page —
  // client_id/redirect_uri/state stay intact because we never navigate away,
  // just reset local state; useQuery() re-reads them from the still-current URL.
  const onSwitchAccount = async () => {
    await handleSignout();
    setConsentInfo(null);
    setLoadError('');
    setLoginError('');
    setLoggedIn(false);
  };

  const onDecision = async (decision) => {
    setLoading(true);
    try {
      const { data } = await api.post(`${apiBase}/oauth/authorize`, {
        client_id: clientId,
        redirect_uri: redirectUri,
        state,
        decision,
      });
      window.location.href = data.redirect_url;
    } catch (error) {
      setLoadError(error?.response?.data?.error?.message || 'เกิดข้อผิดพลาด');
      setLoading(false);
    }
  };

  if (!clientId || !redirectUri) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">ลิงก์ไม่ถูกต้อง: ต้องระบุ client_id และ redirect_uri</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
      }}
    >
      <Paper elevation={8} sx={{ p: 4, maxWidth: 420, width: '100%' }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          CorePlan
        </Typography>

        {!loggedIn && (
          <form onSubmit={handleSubmit(onLoginSubmit)}>
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                เข้าสู่ระบบ CorePlan เพื่อดำเนินการต่อ
              </Typography>
              <TextField label="Username" autoFocus fullWidth {...register('username', { required: true })} />
              <TextField label="Password" type="password" fullWidth {...register('password', { required: true })} />
              {loginError && <Typography color="error" variant="body2">{loginError}</Typography>}
              <Button type="submit" variant="contained" fullWidth>เข้าสู่ระบบ</Button>
            </Stack>
          </form>
        )}

        {loggedIn && loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {loggedIn && !loading && loadError && (
          <Typography color="error" sx={{ mt: 2 }}>{loadError}</Typography>
        )}

        {loggedIn && !loading && consentInfo && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>{consentInfo.app_name}</strong> ขอเข้าถึงข้อมูลของคุณ
            </Typography>
            <List dense sx={{ maxHeight: 320, overflowY: 'auto' }}>
              {consentInfo.claims_preview.map((item) => (
                <ListItem disableGutters key={item.label}>
                  <ListItemIcon sx={{ minWidth: 32 }}><CheckCircleOutlineIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary={item.label} secondary={item.value} />
                </ListItem>
              ))}
            </List>
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="outlined" fullWidth onClick={() => onDecision('deny')}>ปฏิเสธ</Button>
              <Button variant="contained" fullWidth onClick={() => onDecision('allow')}>อนุญาต</Button>
            </Stack>
          </Box>
        )}

        {loggedIn && (
          <Button size="small" onClick={onSwitchAccount} sx={{ mt: 2 }}>
            ไม่ใช่คุณ? เปลี่ยนบัญชี
          </Button>
        )}
      </Paper>
    </Box>
  );
}
